import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AmenityCard } from "../components/AmenityCard";
import { CtaBlock } from "../components/CtaBlock";
import { PageHero } from "../components/PageHero";
import { findPhoto } from "../lib/photos";
import { useSeo } from "../lib/seo";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Amenities() {
  const [amenities, setAmenities] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [a, p] = await Promise.all([
          axios.get(`${API}/amenities`).then((r) => r.data),
          axios.get(`${API}/photos`).then((r) => r.data),
        ]);
        if (!alive) return;
        setAmenities(a || []);
        setPhotos(p.photos || []);
      } catch (e) {
        console.error("amenities fetch fail", e);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const hero = useMemo(
    () => findPhoto(photos, "westbridge-inn-suites-centerville-iowa-indoor-pool-wide"),
    [photos],
  );

  const canonical = "https://westbridgehotels.com/amenities";
  useSeo({
    title: "Amenities — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "Fourteen honest amenities included with every night: indoor pool, free breakfast, free Wi-Fi, free parking, pet-friendly, 24-hour front desk, meeting room, and more.",
    canonical,
    jsonLd: [buildBreadcrumbLd([{ label: "Amenities", to: "/amenities" }])],
  });

  return (
    <main id="main">
      <PageHero
        eyebrow="Amenities"
        title="Everything included. Nothing hidden."
        intro="The stuff you actually use, included with every night. No parking fees, no breakfast upsells, no resort fees pretending to be resort fees."
        photo={hero}
        breadcrumbs={[{ label: "Amenities", to: "/amenities" }]}
        testId="amenities-hero"
      />

      <section className="bg-cream py-16 md:py-24" data-testid="amenities-grid">
        <div className="container-app">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-3">All fourteen</p>
            <h2 className="font-display font-light text-3xl md:text-5xl text-forest-900 tracking-tight leading-tight">
              Real amenities, real photos, honest hours.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {amenities.map((a) => (
              <AmenityCard
                key={a.slug}
                amenity={a}
                photo={findPhoto(photos, a.photo_slug)}
              />
            ))}
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Any questions?"
        title="Call the front desk — we'll pick up."
        body="A real person is at the desk 24 hours a day. Ask about the pool schedule, crew rates, or whether the meeting room is open on a Saturday."
      />
    </main>
  );
}
